import FormattedMessage from 'I18n/components/FormattedMessage';
import { usePortalIdParser } from '../../../../hooks/usePortalIdParser';
import { COMPONENT_NAMES } from '../../../../shared/constants';
import { ComponentViewTracker } from '../../../../utils/UsageTracking/ComponentViewTracker';
import { TrackedButton } from '../../../../utils/UsageTracking/TrackedButton';
import { useIsUngatedForHubSpotCreditsInboundGate } from '../../../../shared/gates';

/**
 * Type for feature groups supported by CreditsCta
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Legacy CreditsCta implementation (before gate)
 */
function LegacyCreditsCta({
  isAdmin,
  billingType,
  usageTracker,
  userRecentlyPurchasedCredits = false,
  screen = '',
  size = 'sm'
}) {
  const portalId = usePortalIdParser();

  // Don't render anything if not admin and billing type is TIER_UPGRADE, or if billing type is either OVERAGE or undefined
  if (!isAdmin && billingType === 'TIER_UPGRADE' || billingType === 'OVERAGE' || !billingType) {
    return null;
  }

  // Determine button text and URL based on conditions
  let message;
  let href;
  let testId;
  if (isAdmin && (billingType === 'TIER_UPGRADE' || userRecentlyPurchasedCredits)) {
    // Admin with tier upgrade or recently purchased credits: View usage in account & billing
    message = 'creditsAwareness.creditsCta.viewUsage';
    href = `/account-and-billing/${portalId}/usage/hubspot-credits`;
    testId = 'credits-cta-view-usage';
  } else {
    // Admin without tier upgrade: Buy credits
    // Non-admin without tier upgrade: Request credits
    href = `/pricing/${portalId}/hubspot-credits`;
    if (isAdmin) {
      message = 'creditsAwareness.creditsCta.buyCredits';
      testId = 'credits-cta-buy-credits';
    } else {
      message = 'creditsAwareness.creditsCta.requestCredits';
      testId = 'credits-cta-request-credits';
    }
  }
  return /*#__PURE__*/_jsx(ComponentViewTracker, {
    usageTracker: usageTracker,
    componentName: COMPONENT_NAMES.CREDITS_CTA,
    screen: screen,
    children: /*#__PURE__*/_jsx(TrackedButton, {
      usageTracker: usageTracker,
      componentName: COMPONENT_NAMES.CREDITS_CTA,
      screen: screen,
      href: href,
      size: size,
      use: "tertiary",
      "data-test-id": testId,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      })
    })
  });
}

/**
 * CreditsCta implementation (after gate)
 */
function CreditsCtaImpl({
  isAdmin,
  billingType,
  percentCreditsUsed,
  usageTracker,
  userRecentlyPurchasedCredits = false,
  screen = '',
  size = 'sm'
}) {
  const portalId = usePortalIdParser();

  // Don't render anything if:
  // 1. No billing type, OR
  // 2. Non-admin with TIER_UPGRADE billing, OR
  // 3. Non-admin with OVERAGE billing NOT at exactly 100% limit, OR
  // 4. Non-admin with DISABLED billing NOT at exactly 100% limit

  if (!billingType || !isAdmin && billingType === 'TIER_UPGRADE' || !isAdmin && billingType === 'OVERAGE' && percentCreditsUsed !== 100 || !isAdmin && billingType === 'DISABLED' && percentCreditsUsed !== 100) {
    return null;
  }

  // Determine button text and URL based on conditions
  let message;
  let href;
  let testId;
  if (isAdmin && billingType === 'TIER_UPGRADE') {
    // Admin with tier upgrade billing: Manage usage
    message = 'creditsAwareness.creditsCta.manageUsage';
    href = `/account-and-billing/${portalId}/usage/hubspot-credits`;
    testId = 'credits-cta-manage-usage';
  } else if (isAdmin && billingType === 'DISABLED') {
    // Admin with DISABLED (included credits) billing
    if (percentCreditsUsed === 100) {
      // Admin reached limit: Add credits
      message = 'creditsAwareness.creditsCta.addCredits';
      href = `/pricing/${portalId}/hubspot-credits`;
      testId = 'credits-cta-add-credits';
    } else {
      // Admin not at limit: Manage usage
      message = 'creditsAwareness.creditsCta.manageUsage';
      href = `/account-and-billing/${portalId}/usage/hubspot-credits`;
      testId = 'credits-cta-manage-usage';
    }
  } else if (isAdmin && (billingType === 'OVERAGE' || userRecentlyPurchasedCredits)) {
    // Admin with overage billing or recently purchased credits: View usage in account & billing
    message = 'creditsAwareness.creditsCta.viewUsage';
    href = `/account-and-billing/${portalId}/usage/hubspot-credits`;
    testId = 'credits-cta-view-usage';
  } else {
    // Non-admin with OVERAGE/DISABLED at 100%: Request credits
    message = 'creditsAwareness.creditsCta.requestCredits';
    href = `/pricing/${portalId}/hubspot-credits`;
    testId = 'credits-cta-request-credits';
  }
  return /*#__PURE__*/_jsx(ComponentViewTracker, {
    usageTracker: usageTracker,
    componentName: COMPONENT_NAMES.CREDITS_CTA,
    screen: screen,
    children: /*#__PURE__*/_jsx(TrackedButton, {
      usageTracker: usageTracker,
      componentName: COMPONENT_NAMES.CREDITS_CTA,
      screen: screen,
      href: href,
      size: size,
      use: "tertiary",
      "data-test-id": testId,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      })
    })
  });
}

/**
 * CreditsCta component for credit-related actions
 *
 * Displayed in CreditBalancePopover, CreditStatusBanner, and FeaturePausedPopover
 */
export function CreditsCta(props) {
  const isUngatedForCreditsInbound = useIsUngatedForHubSpotCreditsInboundGate();
  if (isUngatedForCreditsInbound) {
    return /*#__PURE__*/_jsx(CreditsCtaImpl, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(LegacyCreditsCta, Object.assign({}, props));
}